/**
 * Liberty Signal UI — Sound Engine
 * Preloads WAV files, plays with minimal latency, respects mute preference.
 * Handles browser autoplay policy via user-gesture unlock.
 */

const STORAGE_KEY = "sst_sound_muted";
const VOLUME_KEY = "sst_sound_volume";

let muted = localStorage.getItem(STORAGE_KEY) === "true";
let volume = parseFloat(localStorage.getItem(VOLUME_KEY) || "0.25");
const preloaded = {};
let listeners = [];
let unlocked = false;

// Throttle hover sounds to max 1 per 80ms
let lastHoverTime = 0;
const HOVER_THROTTLE_MS = 80;

/**
 * Some browsers suspend audio until a user gesture triggers playback.
 * We attempt a silent play on the first user interaction to "unlock" audio.
 */
const unlockAudio = () => {
  if (unlocked) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
    if (ctx.state === "suspended") ctx.resume();
    unlocked = true;
  } catch {
    const any = Object.values(preloaded)[0];
    if (any) {
      const clone = any.cloneNode();
      clone.volume = 0;
      clone.play().then(() => { unlocked = true; }).catch(() => {});
    }
  }
  document.removeEventListener("click", unlockAudio, true);
  document.removeEventListener("keydown", unlockAudio, true);
  document.removeEventListener("touchstart", unlockAudio, true);
};

// Register unlock listeners immediately
document.addEventListener("click", unlockAudio, true);
document.addEventListener("keydown", unlockAudio, true);
document.addEventListener("touchstart", unlockAudio, true);

/** Preload a set of sound IDs into Audio objects for fast cloning. */
export const preload = (...ids) => {
  ids.forEach((id) => {
    if (!preloaded[id]) {
      const audio = new Audio(`/sounds/${id}.wav`);
      audio.preload = "auto";
      preloaded[id] = audio;
    }
  });
};

/** Play a sound by ID. No-ops if muted or not preloaded. */
export const play = (id) => {
  if (muted) return;
  const src = preloaded[id];
  if (!src) return;
  const clone = src.cloneNode();
  clone.volume = volume;
  clone.play().catch(() => {});
};

/** Play hover sound (throttled to avoid spam). */
export const playHover = () => {
  const now = Date.now();
  if (now - lastHoverTime < HOVER_THROTTLE_MS) return;
  lastHoverTime = now;
  play("hover");
};

/** Check if sound is muted. */
export const isMuted = () => muted;

/** Toggle mute state. */
export const toggleMute = () => {
  muted = !muted;
  localStorage.setItem(STORAGE_KEY, String(muted));
  notify();
  // Play a click on unmute so user gets immediate feedback
  if (!muted) play("click");
  return muted;
};

/** Set volume (0–1). */
export const setVolume = (v) => {
  volume = Math.max(0, Math.min(1, v));
  localStorage.setItem(VOLUME_KEY, String(volume));
};

export const getVolume = () => volume;

/** Subscribe to mute state changes. Returns unsubscribe fn. */
export const onMuteChange = (fn) => {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
};

const notify = () => listeners.forEach((fn) => fn(muted));

// Preload core sounds immediately
preload("input", "input_up", "input_down", "input_left", "input_right", "fail", "complete", "perfect", "open", "click", "tab", "back", "success", "error", "notify", "hover", "countdown", "round_start");
